import React, { useState, useEffect } from 'react';
import { X, Activity, Pause, Power, WifiOff, Download } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fmt } from '../../utils/safeDate';
import { isStale, ageLabel } from '../../utils/staleness';
import dataService from '../../services/dataService';
import DateRangePicker, { getAggregation, getAggregationMs } from '../common/DateRangePicker';
import AggregationSelector from '../common/AggregationSelector';

const ACTIVE_CHANNEL_AMPS = 0.5;
const ACTIVE_TOTAL_AMPS = 1.5;

const CHANNEL_COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#3b82f6', '#a855f7', '#f97316', '#84cc16', '#06d6a0', '#ef476f', '#118ab2'];

const readChannels = (latest) => {
  if (!latest) return [];
  return Object.entries(latest)
    .filter(([k]) => /^ch\d{2}_amps$/.test(k))
    .map(([k, amps]) => ({ channel: parseInt(k.slice(2, 4), 10), amps: Number(amps) || 0 }))
    .sort((a, b) => a.channel - b.channel);
};

// Bucket time-series readings by interval, averaging each chNN_amps separately.
// Returns rows ready for Recharts: [{ timestamp, time, ch01_amps, ch05_amps, ... }, ...]
const bucketChannels = (readings, intervalMs, fmtPattern) => {
  if (!readings || readings.length === 0) return { rows: [], activeChannels: [] };
  const bucketMap = new Map();
  readings.forEach((r) => {
    const ts = typeof r.timestamp === 'number' ? r.timestamp : new Date(r.timestamp).getTime();
    if (!ts || isNaN(ts)) return;
    const key = Math.floor(ts / intervalMs) * intervalMs;
    if (!bucketMap.has(key)) bucketMap.set(key, {});
    const bucket = bucketMap.get(key);
    Object.entries(r).forEach(([k, v]) => {
      if (!/^ch\d{2}_amps$/.test(k)) return;
      if (!bucket[k]) bucket[k] = { sum: 0, count: 0 };
      bucket[k].sum += Number(v) || 0;
      bucket[k].count++;
    });
  });

  const rows = [...bucketMap.entries()]
    .sort(([a], [b]) => a - b)
    .map(([ts, bucket]) => {
      const row = { timestamp: ts, time: fmt(ts, fmtPattern) };
      Object.entries(bucket).forEach(([k, v]) => {
        row[k] = parseFloat((v.sum / v.count).toFixed(3));
      });
      return row;
    });

  // Identify channels that crossed the activity threshold at least once
  const activeChannels = new Set();
  rows.forEach((row) => {
    Object.entries(row).forEach(([k, v]) => {
      if (/^ch\d{2}_amps$/.test(k) && v >= ACTIVE_CHANNEL_AMPS) activeChannels.add(k);
    });
  });
  return { rows, activeChannels: [...activeChannels].sort() };
};

const exportCSV = (rows, filename) => {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map((r) => keys.map((k) => r[k] ?? '').join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const StatusBadge = ({ execution }) => {
  const map = {
    ACTIVE:  { cls: 'badge-active', Icon: Activity, label: 'RUNNING' },
    IDLE:    { cls: 'badge-idle', Icon: Pause, label: 'IDLE' },
    OFFLINE: { cls: 'badge bg-slate-500/15 text-slate-400 border-slate-500/20', Icon: WifiOff, label: 'OFFLINE' },
    UNKNOWN: { cls: 'badge bg-slate-500/15 text-slate-400 border-slate-500/20', Icon: Power, label: 'NO SCHEMA' },
  };
  const { cls, Icon, label } = map[execution] || map.UNKNOWN;
  return <span className={cls}><Icon className="w-3 h-3" />{label}</span>;
};

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-800 border border-surface-600/50 rounded-lg p-3 shadow-modal">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-xs font-medium tabular-nums" style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' ? `${entry.value.toFixed(3)} A` : entry.value}
        </p>
      ))}
    </div>
  );
};

const MachineDetail = ({ machine, data, onClose }) => {
  const d = data || machine || {};
  const monitorKind = d.monitorKind || machine.monitorKind || 'unknown';
  const stale = isStale(d.timestamp);

  const [dateRange, setDateRange] = useState({ from: Date.now() - 24 * 3600000, to: Date.now(), hours: 24, label: 'Today' });
  const [aggLevel, setAggLevel] = useState('auto');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  // null = show all active channels (default). A number 1..14 = drill into one channel.
  const [selectedChannel, setSelectedChannel] = useState(null);

  // Fetch readings history when machine or date range changes
  useEffect(() => {
    if (monitorKind !== 'analog') return;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    dataService.getReadingsHistory(machine.id, dateRange.hours)
      .then((rows) => { if (!cancelled) setHistory(rows); })
      .catch((err) => { if (!cancelled) setHistoryError(err); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [machine.id, monitorKind, dateRange]);

  // Execution state (same logic as MachineTile)
  let execution = 'OFFLINE';
  if (!stale) {
    if (monitorKind === 'analog') {
      const channels = readChannels(d.latest);
      const total = Number(d.latest?.totalAmps) || 0;
      const topAmps = channels.length > 0 ? Math.max(...channels.map((c) => c.amps)) : 0;
      execution = topAmps >= ACTIVE_CHANNEL_AMPS || total >= ACTIVE_TOTAL_AMPS ? 'ACTIVE' : 'IDLE';
    } else {
      execution = 'UNKNOWN';
    }
  }

  const chartControls = (
    <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
      <DateRangePicker value={dateRange} onChange={setDateRange} />
      <AggregationSelector value={aggLevel} onChange={setAggLevel} />
    </div>
  );

  const renderAnalog = () => {
    const channels = readChannels(d.latest);
    const total = Number(d.latest?.totalAmps ?? 0);

    const agg = getAggregation(dateRange.hours, aggLevel);
    const intervalMs = getAggregationMs(agg);
    const fmtPattern = intervalMs < 60000 ? 'HH:mm:ss' : dateRange.hours <= 48 ? 'HH:mm' : intervalMs >= 86400000 ? 'MM/dd' : 'MM/dd HH:mm';
    const { rows, activeChannels } = bucketChannels(history, intervalMs, fmtPattern);

    // If a channel is selected, the main chart shows only that channel — even
    // if it's below the 0.5A "active" threshold (a quiet channel can still be
    // diagnostically interesting). Otherwise show every channel that crossed
    // threshold at least once in the range.
    const selectedKey = selectedChannel != null ? `ch${String(selectedChannel).padStart(2, '0')}_amps` : null;
    const chartChannels = selectedKey ? [selectedKey] : activeChannels;

    return (
      <div className="space-y-5">
        {/* Live KPIs */}
        <div className="grid grid-cols-4 gap-3">
          <div className="card p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Current</p>
            <p className="metric-lg mt-1 text-cyan-400">{stale ? '—' : `${total.toFixed(2)} A`}</p>
          </div>
          <div className="card p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Channels</p>
            <p className="metric-lg mt-1 text-slate-200">{channels.length}</p>
          </div>
          <div className="card p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Top Channel</p>
            <p className="metric-lg mt-1 text-slate-200">
              {stale || channels.length === 0
                ? '—'
                : `CH${String([...channels].sort((a, b) => b.amps - a.amps)[0].channel).padStart(2, '0')}`}
            </p>
          </div>
          <div className="card p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Last Push</p>
            <p className="metric-md mt-1 text-slate-300">{ageLabel(d.timestamp)}</p>
          </div>
        </div>

        {/* Per-channel current chart */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h4 className="text-sm font-semibold text-slate-200">
                {selectedChannel != null
                  ? `Current Draw — CH${String(selectedChannel).padStart(2, '0')}`
                  : 'Current Draw — All active channels'}
              </h4>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {historyLoading
                  ? 'Loading…'
                  : historyError
                    ? 'Error loading readings'
                    : selectedChannel != null
                      ? `${rows.length} buckets · CH${String(selectedChannel).padStart(2, '0')} only · agg ${agg}`
                      : `${rows.length} buckets · ${activeChannels.length} active channel${activeChannels.length === 1 ? '' : 's'} · agg ${agg}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {selectedChannel != null && (
                <button
                  onClick={() => setSelectedChannel(null)}
                  className="text-[10px] text-brand-400 hover:text-brand-300 border border-brand-500/30 hover:border-brand-500/50 rounded px-2 py-1 flex items-center gap-1 transition-colors"
                >
                  <X className="w-3 h-3" /> Show all channels
                </button>
              )}
              {rows.length > 0 && (
                <button
                  onClick={() => exportCSV(rows, `${machine.id}_${dateRange.label}`)}
                  className="text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-1"
                >
                  <Download className="w-3 h-3" /> CSV
                </button>
              )}
            </div>
          </div>
          {chartControls}
          {historyError ? (
            <div className="text-center py-10 text-red-400 text-sm">
              <p>Failed to load history.</p>
              <p className="text-[11px] text-red-400/70 mt-1">{historyError.message}</p>
              <p className="text-[10px] text-slate-500 mt-2">
                If this is the first query, Firestore needs a composite index on{' '}
                <code className="font-mono">readings (machineId ASC, timestamp ASC)</code>. Check the console for the auto-create link.
              </p>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              {historyLoading ? 'Loading…' : 'No readings in selected range'}
            </div>
          ) : chartChannels.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              All channels below 0.5 A threshold in selected range — click a channel below to drill in.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={rows}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,42,69,0.5)" />
                <XAxis
                  dataKey="time"
                  tick={{ fill: '#64748b', fontSize: 10 }}
                  axisLine={{ stroke: '#1e2a45' }}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fill: '#64748b', fontSize: 10 }}
                  axisLine={{ stroke: '#1e2a45' }}
                  tickLine={false}
                  label={{ value: 'Current (A)', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10, offset: 10 }}
                  domain={selectedChannel != null ? ['auto', 'auto'] : [0, 'auto']}
                />
                <Tooltip content={<DarkTooltip />} />
                <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 11 }} />
                {chartChannels.map((key) => {
                  const chNum = parseInt(key.slice(2, 4), 10);
                  return (
                    <Line
                      key={key}
                      type="stepAfter"
                      dataKey={key}
                      stroke={CHANNEL_COLORS[(chNum - 1) % CHANNEL_COLORS.length]}
                      name={`CH${String(chNum).padStart(2, '0')}`}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Per-channel cards: live value + sparkline over selected date range.
            Sparklines reuse the same bucketed series the main chart consumes — one
            place to pay for the bucketing, two places to read from it. */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-200">Channels</h4>
            <p className="text-[10px] text-slate-500">
              Live as of {d.latest?.timestamp ? fmt(d.latest.timestamp, 'HH:mm:ss') : '—'}
              {' · '}
              Peak: {dateRange.label}
              {' · '}
              <span className="text-slate-600">click to filter chart</span>
            </p>
          </div>
          {channels.length === 0 ? (
            <p className="text-center text-slate-500 text-sm py-6">No channel data in latest{}</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {channels.map((c) => {
                const key = `ch${String(c.channel).padStart(2, '0')}_amps`;
                let peakInRange = 0;
                for (const row of rows) {
                  const v = Number(row[key]) || 0;
                  if (v > peakInRange) peakInRange = v;
                }
                const everActive = peakInRange >= ACTIVE_CHANNEL_AMPS;
                const liveActive = !stale && c.amps >= ACTIVE_CHANNEL_AMPS;
                const isSelected = selectedChannel === c.channel;
                const baseBg = liveActive ? 'bg-emerald-500/5 border-emerald-500/20'
                  : everActive ? 'bg-surface-700/50 border-surface-600/30'
                  : 'bg-surface-700/30 border-surface-600/15';
                const selectedBg = 'ring-2 ring-brand-500/60 bg-brand-500/10 border-brand-500/30';
                return (
                  <button
                    key={c.channel}
                    type="button"
                    onClick={() => setSelectedChannel((prev) => (prev === c.channel ? null : c.channel))}
                    title={isSelected ? 'Click to show all channels' : `Show CH${String(c.channel).padStart(2, '0')} only`}
                    className={`p-3 rounded-lg border text-left transition-all hover:border-brand-500/40 focus:outline-none focus:ring-2 focus:ring-brand-500/40 ${isSelected ? selectedBg : baseBg}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[10px] font-mono font-bold ${isSelected ? 'text-brand-300' : 'text-slate-400'}`}>
                        CH{String(c.channel).padStart(2, '0')}
                      </span>
                      <div className={`w-1.5 h-1.5 rounded-full ${liveActive ? 'bg-emerald-400' : everActive ? 'bg-cyan-400' : 'bg-slate-600'}`} />
                    </div>
                    <div className="flex items-baseline justify-between gap-2">
                      <p className={`text-sm font-mono tabular-nums ${stale ? 'text-slate-600' : liveActive ? 'text-emerald-300' : 'text-slate-300'}`}>
                        {stale ? '—' : `${c.amps.toFixed(3)} A`}
                      </p>
                      {rows.length > 0 && (
                        <p className="text-[9px] text-slate-500 tabular-nums">peak {peakInRange.toFixed(2)}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPlaceholder = (label) => (
    <div className="card p-8 text-center">
      <h3 className="text-sm font-semibold text-slate-200 mb-2">{label}</h3>
      <p className="text-xs text-slate-400">This monitor type isn't reporting on the v2 schema yet.</p>
    </div>
  );

  const renderBody = () => {
    if (stale) {
      return (
        <div className="card p-8 text-center">
          <WifiOff className="w-10 h-10 mx-auto text-slate-500 mb-3" />
          <h3 className="text-sm font-semibold text-slate-200 mb-1">No live data</h3>
          <p className="text-xs text-slate-400">Last push: {ageLabel(d.timestamp)}</p>
          <p className="text-[11px] text-slate-500 mt-2">
            Bound device: <span className="font-mono">{d.monitorDeviceId || machine.monitorDeviceId || '—'}</span>
          </p>
        </div>
      );
    }
    switch (monitorKind) {
      case 'analog': return renderAnalog();
      case 'digital': return renderPlaceholder('Digital monitoring');
      case 'cnc': return renderPlaceholder('CNC monitoring');
      default: return renderPlaceholder('Unknown monitor type');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in" onClick={onClose}>
      <div className="bg-surface-850 border border-surface-600/30 rounded-xl shadow-modal w-full max-w-5xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-surface-600/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <StatusBadge execution={execution} />
            <div>
              <h2 className="text-lg font-semibold text-slate-100">{d.name || machine.name || machine.id}</h2>
              <p className="text-xs text-slate-500">
                {d.location || machine.location || <span className="text-slate-600">No location</span>}
                {' · '}
                <span className="font-mono">{machine.id}</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-700 transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
          {renderBody()}
        </div>
      </div>
    </div>
  );
};

export default MachineDetail;
